import React, { useMemo, useState } from 'react';
import { Cell, OperationCell } from '@/components/Bicester/Table/Cells';
import Picker from '@/components/Bicester/RsPicker';
import Input from '@/components/Bicester/RsInput';
import _ from 'lodash';
import PassTips from './PassTips';
import useSubscribeOption from '@/hooks/preload/useSubscribeOption';
import options from '@/utils/options';
import { SUBSCRIBE_OPTION } from '../constants';
import { Operation, OPERATION_TYPE, SUBSCRIBE_STATUS } from './constants';
import { Alert, IconButton } from 'rsuite';
import { useModal } from '@ebay/nice-modal-react';
import { Button, ConfirmModal } from '@/components/Bicester';
import dayjs from 'dayjs';
import { BICESTER_FULL_DATE } from '@/constants/dateFormat';
import Image from '@/components/Bicester/Image';
import { SERVICE_STATUS } from '@/services/constants';
import { getOptionData } from '../utils';
import DatePickerField from '@/components/shared/DatePicker';
import { isBefore, subDays } from 'date-fns';

export const SubscribeDateCell = ({
  rowData,
  dataKey,
  displayFormat,
  editFormat,
  setData,
  onChangeRowData = () => null,
  onChangeDate,
  onSend,
  onReGet,
  ...props
}: any) => {
  const editing = rowData?.isEditDate;
  const data = _.get(rowData, dataKey, '');

  const [oldDate, setOldDate] = useState('');

  // 切换编辑状态
  const toggleEdit = () => {
    setData((state) => {
      return state.map((item) => {
        if (item.id === rowData?.id) {
          return { ...item, isEditDate: !item?.isEditDate };
        } else {
          return { ...item, hideBtn: !item?.hideBtn };
        }
      });
    });
    setOldDate(editing ? '' : rowData?.date);
  };

  const renderEditCom = useMemo(() => {
    return (
      <div className="subscribe-manage-date-cell-edit">
        <DatePickerField
          style={{ height: 30 }}
          value={!_.isNil(data) ? data : null}
          disabledDate={(date) => isBefore(date, subDays(new Date(), 1))}
          format={editFormat}
          cleanable={false}
          onChange={(val) => onChangeRowData?.(rowData?.id, { [dataKey]: val })}
          placement="topStart"
        />
        {!rowData?.hideBtn && !rowData?.isEdit && (
          <IconButton
            className="min-w-[15px] ml-[5px]"
            onClick={() => {
              if (rowData?.date === oldDate) {
                toggleEdit();
              } else {
                onChangeDate({
                  id: rowData?.subscribe_id,
                  date: rowData?.date || '',
                }).then((res) => {
                  if (res?.status === SERVICE_STATUS.SUCCESS) {
                    Alert.success('保存成功');
                    onReGet();
                  }
                });
              }
            }}
          >
            <Image src="/resources/images/cell-save.svg" />
          </IconButton>
        )}
        {rowData?.is_send === 1 && (
          <Button
            appearance="link"
            style={{ color: '#E16F2C', fontSize: 12, marginLeft: 5 }}
            onClick={() =>
              onSend(rowData?.subscribe_id).then((res) => {
                if (res?.status === SERVICE_STATUS.SUCCESS) {
                  Alert.success('推送成功');
                }
              })
            }
          >
            推送
          </Button>
        )}
      </div>
    );
  }, [data, oldDate]);

  return (
    <Cell {...props} className={`${editing ? 'table-content-editing' : ''}`}>
      {editing ? (
        renderEditCom
      ) : (
        <div className="flex items-center">
          <div className="overflow-hidden overflow-ellipsis whitespace-nowrap">
            {!_.isNil(data) ? dayjs(data).format(displayFormat) : ''}
          </div>
          {!rowData?.hideBtn && !rowData?.isEdit && (
            <IconButton className="min-w-[19px] ml-[5px]" onClick={toggleEdit}>
              <Image src="/resources/images/cell-edit.svg" />
            </IconButton>
          )}
          {rowData?.is_send === 1 && (
            <Button
              appearance="link"
              style={{ color: '#E16F2C', fontSize: 12, marginLeft: 5 }}
              onClick={() =>
                onSend(rowData?.subscribe_id).then((res) => {
                  if (res?.status === SERVICE_STATUS.SUCCESS) {
                    Alert.success('推送成功');
                  }
                })
              }
            >
              推送
            </Button>
          )}
        </div>
      )}
    </Cell>
  );
};

export const OptionCell = ({ rowData, dataKey, onChangeRowData = () => null, ...props }: any) => {
  const editing = rowData?.isEdit;
  const isParking = rowData?.option_id === SUBSCRIBE_OPTION.PARKING;
  const data = _.get(rowData, dataKey, '');

  const renderParking = useMemo(() => {
    return (
      <div className="flex items-center">
        <div className="overflow-hidden overflow-ellipsis whitespace-nowrap">{data}</div>
        {editing ? (
          <Input
            style={{ color: '#E16F2C', fontSize: 12, marginLeft: 5 }}
            value={rowData?.remark}
            onChange={(val) => onChangeRowData?.(rowData?.id, { remark: val })}
          />
        ) : (
          <div className={`text-[12px] text-[#E16F2C] ${rowData?.remark ? 'ml-[5px]' : ''}`}>
            {rowData?.remark}
          </div>
        )}
      </div>
    );
  }, [editing, data, rowData?.remark]);

  return (
    <Cell {...props} className={`editable-cell ${editing ? 'table-content-editing' : ''}`}>
      {isParking ? renderParking : data}
    </Cell>
  );
};

export const TimeSlotCell = ({ rowData, dataKey, onChangeRowData = () => null, ...props }: any) => {
  const editing = rowData?.isEdit;
  const hasOption = [
    SUBSCRIBE_OPTION.PARKING,
    SUBSCRIBE_OPTION.LOUNGE,
    SUBSCRIBE_OPTION.SA,
  ].includes(rowData?.option_id);
  const data = _.get(rowData, dataKey, '');

  const renderEditCom = useMemo(() => {
    if (hasOption) {
      return (
        <Picker
          defaultValue={data}
          data={rowData?.surplusCount?.timeSlot || []}
          cleanable={false}
          onChange={(val) => onChangeRowData?.(rowData?.id, { [dataKey]: val })}
          placement="topStart"
        />
      );
    } else {
      return null;
    }
  }, [data, rowData?.surplusCount]);

  return (
    <Cell {...props} className={`editable-cell ${editing ? 'table-content-editing' : ''}`}>
      {editing ? renderEditCom : data}
    </Cell>
  );
};

export const SubscribeDetailCell = ({
  rowData,
  dataKey,
  onChangeRowData = () => null,
  ...props
}: any) => {
  const editing = rowData?.isEdit;
  const data = _.get(rowData, dataKey, '');

  const renderEditCom = useMemo(() => {
    return options(
      {
        // 7 代客泊车
        [SUBSCRIBE_OPTION.PARKING]: () => (
          <Input
            defaultValue={data}
            onChange={(val) => onChangeRowData?.(rowData?.id, { [dataKey]: val })}
          />
        ),
        // 101 贵宾休息室
        [SUBSCRIBE_OPTION.LOUNGE]: () => (
          <Picker
            defaultValue={data}
            data={rowData?.surplusCount?.content || []}
            cleanable={false}
            onChange={(val) => onChangeRowData?.(rowData?.id, { [dataKey]: val })}
            placement="topStart"
          />
        ),
        // 175 导购大使
        [SUBSCRIBE_OPTION.SA]: () => (
          <Picker
            defaultValue={data}
            data={rowData?.surplusCount?.content || []}
            cleanable={false}
            onChange={(val) => onChangeRowData?.(rowData?.id, { [dataKey]: val })}
            placement="topStart"
          />
        ),
        default: () => null,
      },
      rowData?.option_id
    );
  }, [data, rowData?.option_id, rowData?.surplusCount]);

  return (
    <Cell {...props} className={`editable-cell ${editing ? 'table-content-editing' : ''}`}>
      {editing
        ? renderEditCom
        : options(
            {
              // 7 代客泊车
              7: () => data,
              // 101 贵宾休息室
              101: () => `${data}人`,
              // 175 导购大使
              175: () => `${data}人`,
              default: () => null,
            },
            rowData?.option_id
          )}
    </Cell>
  );
};

export const SubscribeStatusCell = ({ rowData, dataKey, ...props }: any) => {
  const { data: subscribeOption } = useSubscribeOption();

  const data = _.get(rowData, dataKey, '');
  const hasPassed = Boolean(rowData?.pass?.option && rowData?.pass?.option?.length);

  return (
    <Cell {...props}>
      {hasPassed ? (
        <PassTips rowData={rowData} />
      ) : (
        _.find(subscribeOption.status_list, { value: data })?.label || data
      )}
    </Cell>
  );
};

export const SubscribeOperationCell = ({
  rowData,
  data,
  setData,
  onChangeRowData = () => null,
  onGetSurplusCount,
  onSave,
  onUpdateStatus,
  onView,
  onReGet,
  ...props
}: any) => {
  const editing = rowData?.isEdit;
  const hasOption = [
    SUBSCRIBE_OPTION.PARKING,
    SUBSCRIBE_OPTION.LOUNGE,
    SUBSCRIBE_OPTION.SA,
  ].includes(rowData?.option_id);
  const modal = useModal(ConfirmModal);

  const [oldRowData, setOldRowData] = useState(null);

  const operations: any = useMemo(() => {
    const { hideBtn, isEditDate } = rowData;
    // 切换编辑状态
    const toggleEdit = () => {
      if (!editing) {
        setOldRowData(rowData);

        onGetSurplusCount?.(rowData?.date).then((res) => {
          const surplusData = res?.option || [];
          const optionData = getOptionData(surplusData, rowData?.option_id);
          const surplusCount = {
            timeSlot:
              optionData?.time_data?.map((time) => {
                return {
                  value: time?.number,
                  label: time?.time_slot,
                };
              }) || [],
            content: new Array(Number(optionData?.content || 0)).fill(null).map((value, index) => {
              return {
                value: `${index + 1}`,
                label: `${index + 1}人`,
              };
            }),
          };
          onChangeRowData?.(rowData?.id, { surplusCount });
        });
      }
      setData((state) => {
        return state.map((item) => {
          if (item.id === rowData?.id) {
            return { ...item, isEdit: !editing };
          } else {
            return { ...item, hideBtn: !item?.hideBtn };
          }
        });
      });
    };

    // 不保存
    const reset = () => {
      setData((state) => {
        return state.map((item) => {
          if (item.id === rowData?.id) {
            return oldRowData;
          } else {
            return { ...item, hideBtn: !item?.hideBtn };
          }
        });
      });
      setOldRowData(null);
    };

    // 保存
    const save = () => {
      const isParking = rowData?.option_id === SUBSCRIBE_OPTION.PARKING;
      if (isParking) {
        if (rowData?.content?.length < 7) {
          Alert.error('车牌号至少7位字符');
          return;
        } else if (rowData?.content?.length > 8) {
          Alert.error('车牌号至多8位字符');
          return;
        }
      }
      if (
        rowData?.remark === oldRowData?.remark &&
        rowData?.number === oldRowData?.number &&
        rowData?.content === oldRowData?.content
      ) {
        toggleEdit();
        setOldRowData(null);
      } else {
        onSave({
          id: rowData?.id,
          number: rowData?.number || '',
          content: rowData?.content || '',
          ...(isParking && { remark: rowData?.remark || '' }),
        }).then((res) => {
          if (res?.status === SERVICE_STATUS.SUCCESS) {
            Alert.success('保存成功');
            onReGet();
          }
        });
      }
    };

    const updateStatus = (id, statusType) => {
      onUpdateStatus({ id, status: statusType }).then((res) => {
        if (res?.status === SERVICE_STATUS.SUCCESS) {
          Alert.success(`${Operation[statusType]}成功`);
          onReGet();
        }
      });
    };
    // 通过
    const pass = {
      name: Operation[OPERATION_TYPE.PASS],
      onClick: () => updateStatus(rowData?.id, OPERATION_TYPE.PASS),
      show: !hideBtn && !isEditDate,
    };
    // 取消
    const cancel = {
      name: Operation[OPERATION_TYPE.CANCEL],
      onClick: () => {
        modal.show({
          contentText: (
            <>
              <div>
                {`${dayjs(rowData?.date).format(BICESTER_FULL_DATE)} ${rowData?.option_name}`}
              </div>
              <div>取消后无法恢复，确认取消该服务吗？</div>
            </>
          ),
          submitText: '确认取消服务',
          cancelText: '暂不',
          onSubmit: () => updateStatus(rowData?.id, OPERATION_TYPE.CANCEL),
        });
      },
      show: !hideBtn && !isEditDate,
    };
    // 拒绝
    const refuse = {
      name: Operation[OPERATION_TYPE.REFUSE],
      onClick: () => updateStatus(rowData?.id, OPERATION_TYPE.REFUSE),
      show: !hideBtn && !isEditDate,
    };
    // 修改
    const edit = {
      name: '修改',
      onClick: toggleEdit,
      show: !hideBtn && !isEditDate,
    };
    const reviewBtns = options(
      {
        [SUBSCRIBE_STATUS.UNREVIEWED]: () => (hasOption ? [pass, refuse, edit] : [pass, refuse]),
        [SUBSCRIBE_STATUS.PASSED]: () => (hasOption ? [cancel, edit] : [cancel]),
        default: () => [],
      },
      rowData?.status
    );
    const editBtns = [
      {
        name: '不保存',
        onClick: reset,
        show: !hideBtn && !isEditDate,
      },
      {
        name: '保存',
        onClick: save,
        show: !hideBtn && !isEditDate,
      },
    ];
    return editing ? editBtns : reviewBtns;
  }, [rowData]);

  return !rowData?.id ? (
    <Cell {...props}>
      {/* is_update 是否显示更新按钮 1是 0否 是才显示更新确认按钮 */}
      {rowData?.is_update === 1 && (
        <IconButton
          type="submit"
          className="flex items-center"
          disabled={rowData?.is_update_view === 0}
          onClick={() =>
            onView(rowData?.subscribe_id).then((res) => {
              if (res?.status === SERVICE_STATUS.SUCCESS) {
                Alert.success('确认成功');
                onReGet();
              }
            })
          }
        >
          <Image
            src={`/resources/images/subscribe-${
              rowData?.is_update_view === 1 ? 'unconfirm' : 'confirmed'
            }.svg`}
          />
          <div
            style={{
              color: rowData?.is_update_view === 1 ? '#E16F2C' : '#999999',
              marginLeft: 5,
            }}
          >
            {rowData?.is_update_view === 1 ? '有更新待确认' : '更新已确认'}
          </div>
        </IconButton>
      )}
    </Cell>
  ) : (
    <OperationCell operations={operations} {...props} />
  );
};
